import { useRef, useEffect, useState } from 'react';
import {
  ArrowUp,
  ArrowDown,
  CheckCircle,
  XCircle,
  Trash2,
  RefreshCw,
  ChevronsDown,
  Pause
} from 'lucide-react';
import { Frame, FrameType, ControlCodes } from '../../types/protocol';
import { useFrameStore } from '../../store/frameStore';

interface FrameMonitorProps {
  maxFrames?: number;
  showControls?: boolean;
}

const frameTypeLabels: Record<FrameType, string> = {
  [FrameType.TEST_COMMUNICATION]: 'TEST',
  [FrameType.IDENTIFICATION]: 'IDENT',
  [FrameType.TEST_RESPONSE]: 'TRESP',
  [FrameType.MAINTENANCE]: 'MAINT',
  [FrameType.INFORMATION]: 'INFO',
  [FrameType.FLOW_CONTROL]: 'FLOW',
  [FrameType.ERROR]: 'ERROR'
};

const frameTypeColors: Record<FrameType, string> = {
  [FrameType.TEST_COMMUNICATION]: 'text-cyan-400 bg-cyan-400/10',
  [FrameType.IDENTIFICATION]: 'text-purple-400 bg-purple-400/10',
  [FrameType.TEST_RESPONSE]: 'text-blue-400 bg-blue-400/10',
  [FrameType.MAINTENANCE]: 'text-yellow-400 bg-yellow-400/10',
  [FrameType.INFORMATION]: 'text-green-400 bg-green-400/10',
  [FrameType.FLOW_CONTROL]: 'text-gray-400 bg-gray-400/10',
  [FrameType.ERROR]: 'text-red-400 bg-red-400/10'
};

function formatHex(bytes: Uint8Array, maxBytes: number = 16): string {
  const hex = Array.from(bytes.slice(0, maxBytes))
    .map(b => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ');
  return bytes.length > maxBytes ? `${hex} ...` : hex;
}

function formatTimestamp(ts: number): string {
  const date = new Date(ts);
  return date.toLocaleTimeString('es-ES', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3
  });
}

function getTestFrameName(byte: number): string {
  switch (byte) {
    case ControlCodes.TOK: return 'TOK';
    case ControlCodes.TML: return 'TML';
    case ControlCodes.TRT: return 'TRT';
    case ControlCodes.ACK0: return 'ACK0';
    case ControlCodes.ACK1: return 'ACK1';
    case ControlCodes.NAK: return 'NAK';
    default: return `0x${byte.toString(16).toUpperCase()}`;
  }
}

function getFlowControlName(bytes: Uint8Array): string {
  if (bytes.length >= 2 && bytes[0] === ControlCodes.ESC) {
    switch (bytes[1]) {
      case ControlCodes.ACK0: return 'ACK0';
      case ControlCodes.ACK1: return 'ACK1';
      case ControlCodes.NAK: return 'NAK';
      case 0x05: return 'ENQ';
    }
  }
  return 'CTRL';
}

function FrameRow({
  frame,
  isSelected,
  onSelect
}: {
  frame: Frame;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const isTx = frame.direction === 'tx';
  const isValid = frame.decoded.crcValid;
  const needsCRC = frame.type !== FrameType.TEST_COMMUNICATION &&
                   frame.type !== FrameType.FLOW_CONTROL;

  return (
    <div
      className={`
        flex items-center gap-2 p-2 cursor-pointer transition-colors
        border-l-2
        ${isSelected
          ? 'bg-blue-900/30 border-l-blue-500'
          : 'hover:bg-gray-800/50 border-l-transparent'}
      `}
      onClick={onSelect}
    >
      {/* Direction indicator */}
      <div className={`w-6 flex-shrink-0 ${isTx ? 'text-blue-400' : 'text-green-400'}`}>
        {isTx ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
      </div>

      {/* Timestamp */}
      <div className="w-24 text-xs text-gray-500 font-mono flex-shrink-0">
        {formatTimestamp(frame.timestamp)}
      </div>

      {/* Type badge */}
      <div className={`
        w-14 text-xs font-medium px-1.5 py-0.5 rounded text-center flex-shrink-0
        ${frameTypeColors[frame.type]}
      `}>
        {frameTypeLabels[frame.type]}
      </div>

      {/* CRC Status */}
      <div className="w-5 flex-shrink-0">
        {needsCRC && (
          isValid ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <XCircle className="w-4 h-4 text-red-500" />
          )
        )}
      </div>

      {/* Data Preview */}
      <div className="flex-1 text-xs font-mono text-gray-400 truncate min-w-0">
        {frame.type === FrameType.TEST_COMMUNICATION ? (
          <span className="text-cyan-300 font-medium">
            {getTestFrameName(frame.raw[0])}
          </span>
        ) : frame.type === FrameType.FLOW_CONTROL ? (
          <span className="text-gray-300 font-medium">
            {getFlowControlName(frame.raw)}
          </span>
        ) : (
          <span>{formatHex(frame.raw, 10)}</span>
        )}
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
      )}
    </div>
  );
}

export function FrameMonitor({ maxFrames = 50, showControls = true }: FrameMonitorProps) {
  const { frames, selectedFrame, selectFrame, clearFrames, generateMockFrames } = useFrameStore();
  const [autoScroll, setAutoScroll] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  const prevFrameCount = useRef(frames.length);

  const displayFrames = frames.slice(-maxFrames);

  // Auto-scroll to bottom when new frames arrive (if enabled)
  useEffect(() => {
    if (autoScroll && listRef.current && frames.length > prevFrameCount.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
    prevFrameCount.current = frames.length;
  }, [frames.length, autoScroll]);

  // Detect manual scroll to disable auto-scroll
  const handleScroll = () => {
    if (!listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;

    // Re-enable auto-scroll if user scrolls to bottom
    if (isAtBottom && !autoScroll) {
      setAutoScroll(true);
    }
    // Disable auto-scroll if user scrolls up
    else if (!isAtBottom && autoScroll) {
      setAutoScroll(false);
    }
  };

  // Scroll to bottom button handler
  const scrollToBottom = () => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
      setAutoScroll(true);
    }
  };

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden flex flex-col h-full relative">
      {/* Header */}
      <div className="bg-gray-800 px-3 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-300">Monitor de Tramas</span>
          <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
            {frames.length}
          </span>
          {/* Auto-scroll indicator */}
          {!autoScroll && (
            <span className="text-xs text-yellow-500 bg-yellow-500/10 px-2 py-0.5 rounded flex items-center gap-1">
              <Pause className="w-3 h-3" />
              Pausado
            </span>
          )}
        </div>
        {showControls && (
          <div className="flex items-center gap-1">
            <button
              onClick={generateMockFrames}
              className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded transition-colors"
              title="Generar tramas de ejemplo"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={clearFrames}
              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors"
              title="Limpiar tramas"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Instructions */}
      {frames.length === 0 && (
        <div className="p-4 text-center text-gray-500 text-sm">
          <p>Sin tramas capturadas</p>
          <button
            onClick={generateMockFrames}
            className="mt-2 text-blue-400 hover:text-blue-300 underline text-xs"
          >
            Cargar tramas de ejemplo
          </button>
        </div>
      )}

      {/* Frame List */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto"
        onScroll={handleScroll}
      >
        {displayFrames.map((frame, index) => (
          <FrameRow
            key={`${frame.timestamp}-${index}`}
            frame={frame}
            isSelected={selectedFrame === frame}
            onSelect={() => selectFrame(frame)}
          />
        ))}
      </div>

      {/* Scroll to bottom button (shown when not auto-scrolling) */}
      {!autoScroll && frames.length > 0 && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-16 right-4 p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-colors"
          title="Ir al final"
        >
          <ChevronsDown className="w-4 h-4" />
        </button>
      )}

      {/* Legend */}
      <div className="px-3 py-2 border-t border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <ArrowUp className="w-3 h-3 text-blue-400" />
            <span>TX</span>
          </div>
          <div className="flex items-center gap-1">
            <ArrowDown className="w-3 h-3 text-green-400" />
            <span>RX</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span>CRC OK</span>
          </div>
          <div className="ml-auto text-gray-600">
            Click para decodificar
          </div>
        </div>
      </div>
    </div>
  );
}
